import React, { useState, useEffect } from "react";
import {
  ModalComponentAFY,
  InputAFY,
  ButtonAFY,
  IconButtonAFY,
  IconAFY,
  icons,
  variants,
  sizesTextField,
  variantsTextField,
  sizesIcon,
} from "@affinitysystemsgroup/github-packages-affinity-components/dist";
import Grid from "@mui/material/Grid";
import { useForm } from "react-hook-form";

interface ResendDocumentsModalProps {
  openModal: boolean;
  onClose: () => void;
  onConfirm: (policyId: string, emails: string[]) => void;
  policyId: string;
  initialEmail?: string;
}

export const ResendDocumentsModal = ({
  openModal,
  onClose,
  onConfirm,
  policyId,
  initialEmail = "",
}: ResendDocumentsModalProps) => {
  const [emails, setEmails] = useState<string[]>([""]);
  const {
    register,
    formState: { errors },
    trigger,
    getValues,
    setValue,
  } = useForm({
    mode: "onChange",
  });

  // Pre-fill email when modal opens or initialEmail changes
  useEffect(() => {
    if (openModal) {
      if (initialEmail) {
        setEmails([initialEmail]);
        setValue("email_0", initialEmail);
      } else {
        setEmails([""]);
        setValue("email_0", "");
      }
    }
  }, [openModal, initialEmail, setValue]);

  const handleAddEmail = () => {
    setEmails([...emails, ""]);
  };

  const handleRemoveEmail = (index: number) => {
    if (emails.length > 1) {
      const newEmails = emails.filter((_, i) => i !== index);
      setEmails(newEmails);
    }
  };

  const handleConfirm = async () => {
    // Validate all email fields
    const isValid = await trigger();

    if (isValid) {
      const values = getValues();
      const emailList: string[] = [];

      // Collect all non-empty emails
      emails.forEach((_, index) => {
        const emailValue = values[`email_${index}`];
        if (emailValue && emailValue.trim() !== "") {
          emailList.push(emailValue.trim());
        }
      });

      if (emailList.length > 0) {
        onConfirm(policyId, emailList);
        handleModalClose();
      }
    }
  };

  const handleModalClose = () => {
    setEmails([""]);
    setValue("email_0", "");
    onClose();
  };

  const getErrorMessage = (error: any) => {
    return error?.message || "";
  };

  return (
    <ModalComponentAFY
      openModal={openModal}
      onClose={handleModalClose}
      minWidthModal="600px"
      backgroundColor="#FFFFFF"
      BgBackdrop="rgba(0, 0, 0, 0.5)"
      backdropTimeOut={500}
      modalHeight="auto"
      padding={0}
    >
      <div style={{ padding: "24px" }}>
        {/* Header */}
        <div
          style={{
            marginBottom: "20px",
            borderBottom: "1px solid #e0e0e0",
            paddingBottom: "16px",
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: "20px",
              fontWeight: 600,
              color: "#3A56A1",
            }}
          >
            Reenviar documentos
          </h2>
        </div>

        {/* Body */}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <div
              style={{
                fontSize: "14px",
                color: "#666666",
                marginBottom: "20px",
                lineHeight: "1.5",
              }}
            >
              Ingrese los correos electrónicos a los que desea reenviar los
              documentos de la póliza.
            </div>
          </Grid>

          {emails.map((_, index) => (
            <Grid item xs={12} key={index}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={emails.length > 1 ? 10 : 12}>
                  <InputAFY
                    label={`Correo electrónico ${index + 1}`}
                    type="email"
                    placeholder="ejemplo@correo.com"
                    size={sizesTextField.small}
                    fullWidth
                    variant={variantsTextField.filled}
                    id={`email_${index}`}
                    register={register}
                    validations={{
                      required: {
                        value: index === 0,
                        message: "El correo electrónico es requerido",
                      },
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "Formato de correo no válido",
                      },
                    }}
                    error={errors[`email_${index}`] !== undefined}
                    helperText={
                      errors[`email_${index}`]
                        ? getErrorMessage(errors[`email_${index}`])
                        : ""
                    }
                  />
                </Grid>
                {emails.length > 1 && (
                  <Grid item xs={2}>
                    <IconButtonAFY
                      icon={icons.Delete}
                      iconColor="#d32f2f"
                      onClick={() => handleRemoveEmail(index)}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
          ))}

          <Grid item xs={12}>
            <ButtonAFY
              variant={variants.outlined}
              label="Agregar correo"
              color="#3A56A1"
              backgroundColor="#3A56A1"
              icon={icons.Add}
              onClick={handleAddEmail}
            />
          </Grid>

          <Grid item xs={12}>
            <div
              style={{
                backgroundColor: "#E3F2FD",
                padding: "12px",
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <IconAFY
                icon={icons.Info}
                color="#1976D2"
                size={sizesIcon.standard}
              />
              <span style={{ fontSize: "13px", color: "#1976D2" }}>
                Los documentos de la póliza serán enviados a los correos
                electrónicos indicados.
              </span>
            </div>
          </Grid>
        </Grid>

        {/* Footer */}
        <div
          style={{
            marginTop: "24px",
            paddingTop: "16px",
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "flex-end",
            gap: "12px",
          }}
        >
          <ButtonAFY
            variant={variants.outlined}
            label="Cancelar"
            color="#3A56A1"
            onClick={handleModalClose}
          />
          <ButtonAFY
            variant={variants.contained}
            label="Enviar"
            backgroundColor="#3A56A1"
            color="#FFFFFF"
            onClick={handleConfirm}
          />
        </div>
      </div>
    </ModalComponentAFY>
  );
};

